import { useRef, type KeyboardEvent, type ReactNode } from 'react';

import { cn } from '@platform/ui';

export interface TabSpec {
  id: string;
  label: string;
  /** A small count or dot after the label, e.g. the number of failed messages. */
  badge?: ReactNode;
}

export interface TabsProps {
  /** A `useId()` from the parent, shared with every `TabPanel` so the wiring matches. */
  idBase: string;
  tabs: TabSpec[];
  activeId: string;
  onChange: (id: string) => void;
  /** Names the tab list for screen readers, e.g. "Appointment sections". */
  label: string;
  className?: string;
}

/**
 * The tab strip of the appointment detail.
 *
 * Composed here rather than in `@platform/ui` because it is the only screen that needs
 * tabs so far; it moves into the design system the second time something wants it.
 *
 * A real ARIA tablist (rules §48.2): arrow keys move between tabs, Home and End jump to
 * the ends, only the active tab is in the tab order, and each panel is labelled by its
 * tab. Activation follows focus, which is the right choice when the panels are already
 * in memory.
 */
/**
 * The ARIA tab pattern's keyboard half, on its own (M13.7).
 *
 * `Tabs` below is the house strip and most screens use it. Two screens — the form
 * builder's narrow layout and the template editor — draw their own strip in a different
 * recipe (pills, not an underline) and had `role="tab"` with no arrow keys and no roving
 * tabindex: a reader was told "tab 2 of 3" and could only reach it with the mouse. This
 * gives them the keyboard without giving them a different look (WCAG 2.1.1, 4.1.2).
 *
 * `list` is the element carrying `role="tablist"`; the handler moves focus to the nth
 * `[role="tab"]` inside it, so the caller only has to render `tabIndex={selected ? 0 : -1}`.
 */
export function tablistKeyDown<Id extends string>(
  event: KeyboardEvent<HTMLElement>,
  ids: readonly Id[],
  activeId: Id,
  onChange: (id: Id) => void,
  list: HTMLElement | null,
): void {
  const index = ids.indexOf(activeId);
  if (index < 0) return;

  let next: number;
  if (event.key === 'ArrowRight' || event.key === 'ArrowDown') next = (index + 1) % ids.length;
  else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp')
    next = (index - 1 + ids.length) % ids.length;
  else if (event.key === 'Home') next = 0;
  else if (event.key === 'End') next = ids.length - 1;
  else return;

  event.preventDefault();
  const target = ids[next];
  if (target === undefined) return;
  onChange(target);
  list?.querySelectorAll<HTMLButtonElement>('[role="tab"]')[next]?.focus();
}

export function Tabs({ idBase, tabs, activeId, onChange, label, className }: TabsProps) {
  const listRef = useRef<HTMLDivElement>(null);

  const move = (event: KeyboardEvent<HTMLDivElement>): void => {
    const index = tabs.findIndex((tab) => tab.id === activeId);
    if (index < 0) return;

    let next: number;
    if (event.key === 'ArrowRight') next = (index + 1) % tabs.length;
    else if (event.key === 'ArrowLeft') next = (index - 1 + tabs.length) % tabs.length;
    else if (event.key === 'Home') next = 0;
    else if (event.key === 'End') next = tabs.length - 1;
    else return;

    event.preventDefault();
    const target = tabs[next];
    if (target === undefined) return;
    onChange(target.id);
    const buttons = listRef.current?.querySelectorAll<HTMLButtonElement>('[role="tab"]');
    buttons?.[next]?.focus();
  };

  return (
    <div
      ref={listRef}
      role="tablist"
      aria-label={label}
      onKeyDown={move}
      /* `relative` (M11.11): the tabs' own `sr-only` text is absolutely positioned, and
         without a positioned ancestor its box is laid out against the initial containing
         block — escaping this strip's clip and giving the page a phantom sideways scroll
         as wide as the whole tab list. See `ScrollArea` in @platform/ui. */
      className={cn(
        'relative flex gap-1 overflow-x-auto border-b border-neutral-200 pb-px',
        className,
      )}
    >
      {tabs.map((tab) => {
        const selected = tab.id === activeId;
        return (
          <button
            key={tab.id}
            id={`${idBase}-${tab.id}`}
            type="button"
            role="tab"
            aria-selected={selected}
            aria-controls={`${idBase}-${tab.id}-panel`}
            tabIndex={selected ? 0 : -1}
            onClick={() => {
              onChange(tab.id);
            }}
            className={cn(
              /* `max-lg:min-h-11` matches the Button's phone target (rules §49.3). */
              'flex shrink-0 cursor-pointer items-center gap-2 rounded-t-input px-4 py-[10px] text-base font-medium whitespace-nowrap max-lg:min-h-11',
              '-mb-px border-b-2',
              selected
                ? 'border-primary text-primary-accessible'
                : 'border-transparent text-neutral-500 hover:bg-neutral-50 hover:text-neutral-700',
            )}
          >
            {tab.label}
            {tab.badge}
          </button>
        );
      })}
    </div>
  );
}

export interface TabPanelProps {
  /** Matches the `TabSpec.id`. */
  id: string;
  idBase: string;
  active: boolean;
  children: ReactNode;
}

/**
 * One tab's panel. Rendered only while active — a section that loads its own data should
 * not load it while nobody is looking at it.
 */
export function TabPanel({ id, idBase, active, children }: TabPanelProps) {
  if (!active) return null;
  return (
    <div
      role="tabpanel"
      id={`${idBase}-${id}-panel`}
      aria-labelledby={`${idBase}-${id}`}
      tabIndex={0}
      className="pt-6 outline-none"
    >
      {children}
    </div>
  );
}
